import React from "react";
import FieldCard from "./FieldCard";
import InputField from "./InputField";

const CLADDING_OPTIONS = [
  { value: "gypsum", label: "Gypsum / plasterboard" },
  { value: "osb", label: "OSB board" },
  { value: "plywood", label: "Plywood" },
  { value: "mdf", label: "MDF board" },
  { value: "fermacell", label: "Fermacell / fibre gypsum" },
  { value: "cement_board", label: "Cement board / wet room board" },
  { value: "fire_board", label: "Fire-rated board" },
  { value: "acoustic_panel", label: "Acoustic panel / slat panel" },
  { value: "wall_panel", label: "Wall panel / cladding" },
  { value: "custom_cladding", label: "Custom cladding" },
];

const WALL_PROJECT_TYPES = [
  "internal_wall",
  "external_loadbearing_wall",
  "carport_garage",
];

function WallSectionCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
      <div className="mb-3 text-sm font-semibold text-white">{title}</div>
      {children}
    </div>
  );
}

function createDefaultLayer(type = "gypsum") {
  return { type };
}

function expandLayer(layer) {
  if (typeof layer === "string") return [{ type: layer }];

  const type = layer?.type || "gypsum";
  const count = Math.max(1, Number(layer?.layers ?? 1) || 1);

  return Array.from({ length: count }, () => ({ type }));
}

function deriveWallClassification(wall = {}) {
  const wallType = wall.wallType || "internal";

  if (wallType === "external") {
    return {
      wallType: "external",
      wallPlacement: "external",
      wallStructuralRole: "loadbearing",
      wallSystem: wall.wallSystem || "timber",
    };
  }

  if (wallType === "carport") {
    return {
      wallType: "carport",
      wallPlacement: "external",
      wallStructuralRole: "non_loadbearing",
      wallSystem: wall.wallSystem || "timber",
    };
  }

  return {
    wallType: "internal",
    wallPlacement: "internal",
    wallStructuralRole: "non_loadbearing",
    wallSystem: wall.wallSystem || "timber",
  };
}

function deriveProjectTypeFromWall(wall = {}) {
  const classification = deriveWallClassification(wall);

  if (
    classification.wallPlacement === "external" &&
    classification.wallStructuralRole === "loadbearing"
  ) {
    return "external_loadbearing_wall";
  }

  if (classification.wallType === "carport") {
    return "carport_garage";
  }

  return "internal_wall";
}

function deriveProjectTypesFromWalls(walls = []) {
  const types = new Set();

  for (const wall of walls) {
    types.add(deriveProjectTypeFromWall(wall));
  }

  return Array.from(types);
}

function derivePrimaryProjectType(projectTypes = []) {
  if (projectTypes.includes("external_loadbearing_wall")) {
    return "external_loadbearing_wall";
  }

  if (projectTypes.includes("internal_wall")) {
    return "internal_wall";
  }

  if (projectTypes.includes("carport_garage")) {
    return "carport_garage";
  }

  return projectTypes[0] || "internal_wall";
}

function createDefaultWall(base = {}) {
  const classification = deriveWallClassification(base);

  return {
    id: crypto.randomUUID(),
    name: "",
    lengthM: "",
    heightM: "",
    wallCount: 1,
    doorCount: 0,
    doorWidthM: 0.9,
    doorHeightM: 2.1,
    boardLayersPerSide: 1,
    claddingSides: 2,
    wallCladding: {
      sideA: [createDefaultLayer("gypsum")],
      sideB: [createDefaultLayer("gypsum")],
    },
    casingIncluded: false,
    casingSidesPerDoor: 0,
    skirtingIncluded: false,
    skirtingSides: 0,
    insulationRequired: false,
    windBarrierRequired: false,
    wallType: classification.wallType,
    wallPlacement: classification.wallPlacement,
    wallStructuralRole: classification.wallStructuralRole,
    wallSystem: classification.wallSystem,
    ...base,
  };
}

function normalizeLayers(layers, fallbackCount = 1) {
  if (Array.isArray(layers) && layers.length) {
    return layers.flatMap((layer) => expandLayer(layer));
  }

  return Array.from({ length: Math.max(1, Number(fallbackCount) || 1) }, () =>
    createDefaultLayer("gypsum")
  );
}

function normalizeWallForEditor(wall = {}) {
  const claddingSides = Math.max(
    1,
    Math.min(2, Number(wall.claddingSides ?? 2) || 2)
  );

  const sideA =
    wall.wallCladding?.sideA ||
    wall.cladding?.sideA ||
    normalizeLayers(null, wall.boardLayersPerSide || 1);

  const sideB =
    wall.wallCladding?.sideB ||
    wall.cladding?.sideB ||
    (claddingSides === 2
      ? normalizeLayers(null, wall.boardLayersPerSide || 1)
      : []);

  const classification = deriveWallClassification(wall);

  return createDefaultWall({
    ...wall,
    ...classification,
    id: wall.id || crypto.randomUUID(),
    boardLayersPerSide: Math.max(
      Number(wall.boardLayersPerSide ?? 0) || 0,
      sideA.length,
      sideB.length,
      1
    ),
    claddingSides,
    wallCladding: {
      sideA: normalizeLayers(sideA, wall.boardLayersPerSide || 1),
      sideB:
        claddingSides === 2
          ? normalizeLayers(sideB, wall.boardLayersPerSide || 1)
          : [],
    },
    casingIncluded: !!wall.casingIncluded,
    casingSidesPerDoor: wall.casingIncluded
      ? Number(wall.casingSidesPerDoor ?? 2) || 2
      : 0,
    skirtingIncluded: !!wall.skirtingIncluded,
    skirtingSides: wall.skirtingIncluded
      ? Number(wall.skirtingSides ?? claddingSides) || claddingSides
      : 0,
    insulationRequired: !!wall.insulationRequired,
    windBarrierRequired: !!wall.windBarrierRequired,
  });
}

function buildLegacyFieldsFromWall(wall) {
  const firstWall = normalizeWallForEditor(wall);

  return {
    wallLengthM: firstWall.lengthM ?? "",
    wallHeightM: firstWall.heightM ?? "",
    wallCount: firstWall.wallCount ?? 1,
    doorCount: firstWall.doorCount ?? 0,
    doorWidthM: firstWall.doorWidthM ?? 0.9,
    doorHeightM: firstWall.doorHeightM ?? 2.1,
    boardLayersPerSide: firstWall.boardLayersPerSide ?? 1,
    claddingSides: firstWall.claddingSides ?? 2,
    wallCladding: firstWall.wallCladding,
    casingIncluded: !!firstWall.casingIncluded,
    casingSidesPerDoor: firstWall.casingSidesPerDoor ?? 0,
    skirtingIncluded: !!firstWall.skirtingIncluded,
    skirtingSides: firstWall.skirtingSides ?? 0,
    insulationRequired: !!firstWall.insulationRequired,
    windBarrierRequired: !!firstWall.windBarrierRequired,
    wallType: firstWall.wallType || "internal",
    wallPlacement: firstWall.wallPlacement || "internal",
    wallStructuralRole: firstWall.wallStructuralRole || "non_loadbearing",
    wallSystem: firstWall.wallSystem || "timber",
  };
}

function SideLayerEditor({
  title,
  layers,
  onAdd,
  onRemove,
  onChange,
  disabled = false,
}) {
  return (
    <div
      className={`rounded-lg border border-gray-700 p-3 ${
        disabled ? "opacity-50" : ""
      }`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-medium text-white">{title}</div>
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-700"
        >
          Add layer
        </button>
      </div>

      <div className="space-y-2">
        {layers.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-700 px-3 py-2 text-sm text-gray-400">
            No cladding on this side.
          </div>
        ) : (
          layers.map((layer, layerIndex) => (
            <div
              key={`${title}-layer-${layerIndex}`}
              className="grid gap-2 md:grid-cols-[1fr_auto]"
            >
              <select
                value={layer?.type || "gypsum"}
                onChange={(e) => onChange(layerIndex, e.target.value)}
                disabled={disabled}
                className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
              >
                {CLADDING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => onRemove(layerIndex)}
                disabled={disabled || layers.length === 0}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-700"
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function WallEditor({ parsedData, setParsedData, errors = {} }) {
  const walls =
    Array.isArray(parsedData?.walls) && parsedData.walls.length
      ? parsedData.walls.map(normalizeWallForEditor)
      : [
          normalizeWallForEditor({
            name: "",
            lengthM: parsedData.wallLengthM ?? "",
            heightM: parsedData.wallHeightM ?? "",
            wallCount: parsedData.wallCount ?? 1,
            doorCount: parsedData.doorCount ?? 0,
            doorWidthM: parsedData.doorWidthM ?? 0.9,
            doorHeightM: parsedData.doorHeightM ?? 2.1,
            boardLayersPerSide: parsedData.boardLayersPerSide ?? 1,
            claddingSides: parsedData.claddingSides ?? 2,
            wallCladding:
              parsedData.wallCladding ||
              (parsedData.boardLayersPerSide
                ? {
                    sideA: Array.from(
                      { length: Number(parsedData.boardLayersPerSide) || 1 },
                      () => createDefaultLayer("gypsum")
                    ),
                    sideB:
                      Number(parsedData.claddingSides ?? 2) === 2
                        ? Array.from(
                            {
                              length:
                                Number(parsedData.boardLayersPerSide) || 1,
                            },
                            () => createDefaultLayer("gypsum")
                          )
                        : [],
                  }
                : undefined),
            casingIncluded: !!parsedData.casingIncluded,
            casingSidesPerDoor: parsedData.casingSidesPerDoor ?? 0,
            skirtingIncluded: !!parsedData.skirtingIncluded,
            skirtingSides: parsedData.skirtingSides ?? 0,
            insulationRequired: !!parsedData.insulationRequired,
            windBarrierRequired: !!parsedData.windBarrierRequired,
            wallType: parsedData.wallType || "internal",
            wallPlacement: parsedData.wallPlacement,
            wallStructuralRole: parsedData.wallStructuralRole,
            wallSystem: parsedData.wallSystem || "timber",
          }),
        ];

  const setWalls = (nextWalls) => {
    const normalizedWalls = (Array.isArray(nextWalls) ? nextWalls : []).map(
      normalizeWallForEditor
    );

    const firstWall = normalizedWalls[0] || createDefaultWall();
    const wallProjectTypes = deriveProjectTypesFromWalls(normalizedWalls);
    const wallPrimaryProjectType = derivePrimaryProjectType(wallProjectTypes);

    setParsedData((prev) => {
      const prevProjectTypes = Array.isArray(prev?.projectTypes)
        ? prev.projectTypes
        : prev?.projectType
        ? [prev.projectType]
        : [];

      const nonWallProjectTypes = prevProjectTypes.filter(
        (type) => !WALL_PROJECT_TYPES.includes(type)
      );

      const mergedProjectTypes = [
        ...new Set([...nonWallProjectTypes, ...wallProjectTypes]),
      ];

      const previousPrimary =
        prev?.primaryProjectType || prev?.projectType || null;
      const previousPrimaryIsWall = WALL_PROJECT_TYPES.includes(previousPrimary);

      const nextPrimaryProjectType =
        !previousPrimary || previousPrimaryIsWall
          ? wallPrimaryProjectType
          : previousPrimary;

      return {
        ...prev,
        projectType: nextPrimaryProjectType,
        primaryProjectType: nextPrimaryProjectType,
        projectTypes: mergedProjectTypes,
        walls: normalizedWalls,
        ...buildLegacyFieldsFromWall(firstWall),
      };
    });
  };

  const addWall = () => {
    setWalls([...walls, createDefaultWall()]);
  };

  const updateWall = (index, field, value) => {
    setWalls(
      walls.map((wall, i) =>
        i === index ? normalizeWallForEditor({ ...wall, [field]: value }) : wall
      )
    );
  };

  const updateWallObject = (index, patch) => {
    setWalls(
      walls.map((wall, i) =>
        i === index ? normalizeWallForEditor({ ...wall, ...patch }) : wall
      )
    );
  };

  const updateWallType = (index, wallType) => {
    if (wallType === "external") {
      updateWallObject(index, {
        wallType: "external",
        wallPlacement: "external",
        wallStructuralRole: "loadbearing",
        wallSystem: "timber",
        casingIncluded: false,
        casingSidesPerDoor: 0,
        skirtingIncluded: false,
        skirtingSides: 0,
      });
      return;
    }

    if (wallType === "carport") {
      updateWallObject(index, {
        wallType: "carport",
        wallPlacement: "external",
        wallStructuralRole: "non_loadbearing",
        wallSystem: "timber",
        casingIncluded: false,
        casingSidesPerDoor: 0,
        skirtingIncluded: false,
        skirtingSides: 0,
      });
      return;
    }

    updateWallObject(index, {
      wallType: "internal",
      wallPlacement: "internal",
      wallStructuralRole: "non_loadbearing",
      wallSystem: "timber",
    });
  };

  const updateWallCladdingSide = (wallIndex, sideKey, nextLayers) => {
    setWalls(
      walls.map((wall, i) => {
        if (i !== wallIndex) return wall;

        const nextWall = normalizeWallForEditor({
          ...wall,
          wallCladding: {
            ...wall.wallCladding,
            [sideKey]: nextLayers,
          },
        });

        const sideACount = nextWall.wallCladding.sideA.length;
        const sideBCount = nextWall.wallCladding.sideB.length;

        return {
          ...nextWall,
          boardLayersPerSide: Math.max(sideACount, sideBCount, 1),
        };
      })
    );
  };

  const addLayer = (wallIndex, sideKey) => {
    const wall = walls[wallIndex];
    const currentLayers = wall.wallCladding?.[sideKey] || [];
    updateWallCladdingSide(wallIndex, sideKey, [
      ...currentLayers,
      createDefaultLayer("gypsum"),
    ]);
  };

  const removeLayer = (wallIndex, sideKey, layerIndex) => {
    const wall = walls[wallIndex];
    const currentLayers = wall.wallCladding?.[sideKey] || [];
    const nextLayers = currentLayers.filter((_, i) => i !== layerIndex);
    updateWallCladdingSide(wallIndex, sideKey, nextLayers);
  };

  const updateLayerType = (wallIndex, sideKey, layerIndex, type) => {
    const wall = walls[wallIndex];
    const currentLayers = wall.wallCladding?.[sideKey] || [];
    const nextLayers = currentLayers.map((layer, i) =>
      i === layerIndex ? { ...layer, type } : layer
    );
    updateWallCladdingSide(wallIndex, sideKey, nextLayers);
  };

  const removeWall = (index) => {
    const nextWalls = walls.filter((_, i) => i !== index);
    setWalls(nextWalls.length ? nextWalls : [createDefaultWall()]);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-400">
        Define one or more wall sections. Use finished wall dimensions, not floor
        area. You can set different cladding and layer build-ups on each side of
        the wall.
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={addWall}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add wall section
        </button>
      </div>

      <div className="space-y-4">
        {walls.map((wall, index) => {
          const sideA = wall.wallCladding?.sideA || [];
          const sideB = wall.wallCladding?.sideB || [];
          const isSingleSided = Number(wall.claddingSides || 2) === 1;
          const isExteriorWall = wall.wallType === "external";
          const isCarportWall = wall.wallType === "carport";
          const showInteriorTrimFields = !isExteriorWall && !isCarportWall;

          return (
            <WallSectionCard
              key={wall.id || `wall-${index}`}
              title={wall.name?.trim() || `Wall section ${index + 1}`}
            >
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <FieldCard label="Section name">
                  <InputField
                    value={wall.name ?? ""}
                    onChange={(e) => updateWall(index, "name", e.target.value)}
                    placeholder="e.g. Bedroom partition"
                  />
                </FieldCard>

                <FieldCard
                  label="Length"
                  unit="m"
                  error={errors[`walls.${index}`]}
                >
                  <InputField
                    type="number"
                    value={wall.lengthM ?? ""}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "lengthM",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    placeholder="e.g. 4.2"
                  />
                </FieldCard>

                <FieldCard
                  label="Height"
                  unit="m"
                  error={errors[`walls.${index}`]}
                >
                  <InputField
                    type="number"
                    value={wall.heightM ?? ""}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "heightM",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    placeholder="e.g. 2.4"
                  />
                </FieldCard>

                <FieldCard label="Wall count">
                  <InputField
                    type="number"
                    value={wall.wallCount ?? 1}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "wallCount",
                        e.target.value === "" ? 1 : Number(e.target.value)
                      )
                    }
                    placeholder="e.g. 1"
                  />
                </FieldCard>

                <FieldCard label="Door count">
                  <InputField
                    type="number"
                    value={wall.doorCount ?? 0}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "doorCount",
                        e.target.value === "" ? 0 : Number(e.target.value)
                      )
                    }
                    placeholder="e.g. 1"
                  />
                </FieldCard>

                <FieldCard label="Door width" unit="m">
                  <InputField
                    type="number"
                    value={wall.doorWidthM ?? ""}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "doorWidthM",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    placeholder="e.g. 0.9"
                  />
                </FieldCard>

                <FieldCard label="Door height" unit="m">
                  <InputField
                    type="number"
                    value={wall.doorHeightM ?? ""}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "doorHeightM",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    placeholder="e.g. 2.1"
                  />
                </FieldCard>

                <FieldCard label="Wall type">
                  <select
                    value={wall.wallType || "internal"}
                    onChange={(e) => updateWallType(index, e.target.value)}
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="internal">Internal wall</option>
                    <option value="external">External loadbearing wall</option>
                    <option value="carport">Carport wall</option>
                  </select>
                </FieldCard>

                <FieldCard label="Cladding sides">
                  <select
                    value={wall.claddingSides ?? 2}
                    onChange={(e) =>
                      updateWall(index, "claddingSides", Number(e.target.value))
                    }
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>1 side</option>
                    <option value={2}>2 sides</option>
                  </select>
                </FieldCard>

                <FieldCard label="Insulation required">
                  <select
                    value={wall.insulationRequired ? "yes" : "no"}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "insulationRequired",
                        e.target.value === "yes"
                      )
                    }
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </FieldCard>

                <FieldCard label="Wind barrier required">
                  <select
                    value={wall.windBarrierRequired ? "yes" : "no"}
                    onChange={(e) =>
                      updateWall(
                        index,
                        "windBarrierRequired",
                        e.target.value === "yes"
                      )
                    }
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </FieldCard>

                {showInteriorTrimFields && (
                  <>
                    <FieldCard label="Casing included">
                      <select
                        value={wall.casingIncluded ? "yes" : "no"}
                        onChange={(e) =>
                          updateWall(
                            index,
                            "casingIncluded",
                            e.target.value === "yes"
                          )
                        }
                        className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </FieldCard>

                    <FieldCard label="Casing sides per door">
                      <InputField
                        type="number"
                        value={wall.casingSidesPerDoor ?? 0}
                        onChange={(e) =>
                          updateWall(
                            index,
                            "casingSidesPerDoor",
                            e.target.value === "" ? 0 : Number(e.target.value)
                          )
                        }
                        placeholder="e.g. 2"
                      />
                    </FieldCard>

                    <FieldCard label="Skirting included">
                      <select
                        value={wall.skirtingIncluded ? "yes" : "no"}
                        onChange={(e) =>
                          updateWall(
                            index,
                            "skirtingIncluded",
                            e.target.value === "yes"
                          )
                        }
                        className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </FieldCard>

                    <FieldCard label="Skirting sides">
                      <InputField
                        type="number"
                        value={wall.skirtingSides ?? 0}
                        onChange={(e) =>
                          updateWall(
                            index,
                            "skirtingSides",
                            e.target.value === "" ? 0 : Number(e.target.value)
                          )
                        }
                        placeholder="e.g. 2"
                      />
                    </FieldCard>
                  </>
                )}
              </div>

              <div className="mt-6">
                <div className="mb-3 text-sm font-semibold text-white">
                  Wall cladding / build-up
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <SideLayerEditor
                    title="Side A"
                    layers={sideA}
                    onAdd={() => addLayer(index, "sideA")}
                    onRemove={(layerIndex) =>
                      removeLayer(index, "sideA", layerIndex)
                    }
                    onChange={(layerIndex, type) =>
                      updateLayerType(index, "sideA", layerIndex, type)
                    }
                  />

                  <SideLayerEditor
                    title="Side B"
                    layers={sideB}
                    onAdd={() => addLayer(index, "sideB")}
                    onRemove={(layerIndex) =>
                      removeLayer(index, "sideB", layerIndex)
                    }
                    onChange={(layerIndex, type) =>
                      updateLayerType(index, "sideB", layerIndex, type)
                    }
                    disabled={isSingleSided}
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeWall(index)}
                  className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  Remove section
                </button>
              </div>
            </WallSectionCard>
          );
        })}
      </div>
    </div>
  );
}